import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useDesign } from '../../contexts/DesignContext';

const Panel = styled.div`
    width: 380px; /* Increased width slightly */
    background-color: #ffffff;
    border-left: 1px solid #e9ecef;
    padding: 20px;
    box-shadow: -2px 0 5px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    border-radius: 8px;
    height: 100%; /* Make it fill the height of its container if needed */
    box-sizing: border-box; /* Include padding in height */

    h2 {
        margin-top: 0;
        color: #343a40;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 10px;
        margin-bottom: 20px;
    }

    h3 {
        color: #007bff;
        margin-bottom: 15px;
    }
`;

const PropertySection = styled.div`
    margin-bottom: 15px;
    label {
        display: block;
        margin-bottom: 5px;
        font-weight: bold;
        color: #495057;
    }
`;

const PropertyInput = styled.input`
    margin: 5px 0;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: calc(100% - 10px); /* Adjust width for padding */
    box-sizing: border-box; /* Ensures padding is included in the width */
`;

const PropertyTextArea = styled.textarea`
    width: calc(100% - 10px);
    padding: 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.9em;
    box-sizing: border-box;
    min-height: 200px; /* Increased min-height for more JSON visibility */
    font-family: monospace; /* For better readability of JSON */
`;

const SaveButton = styled.button`
    width: 100%;
    padding: 10px 15px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1em;
    margin-top: 20px;
    &:hover {
        background-color: #218838;
    }
`;

const CloseButton = styled.button`
    width: 100%;
    padding: 10px 15px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1em;
    margin-top: 10px;
    &:hover {
        background-color: #c82333;
    }
`;

// Helper function to safely parse JSON
const safeParseJSON = (jsonString) => {
    try {
        const parsed = JSON.parse(jsonString);
        // Ensure the parsed result is an object (or null if that's acceptable for empty properties)
        if (typeof parsed === 'object' && parsed !== null) {
            return parsed;
        }
        console.error("Parsed JSON is not an object or is null:", parsed);
        return null;
    } catch (e) {
        console.error("Invalid JSON:", e);
        return null; // Return null on error
    }
};

function PropertiesPanel({ selectedComponentId, designId, onUpdateComponent, onClose }) {
    const { design } = useDesign();
    const [properties, setProperties] = useState({});
    const [label, setLabel] = useState('');
    const [propertiesJson, setPropertiesJson] = useState('{}'); // State for JSON string
    const selectedComponent = design?.components.find((c) => c.id === selectedComponentId);

    // Effect to initialize state when selectedComponent changes
    useEffect(() => {
        if (selectedComponent) {
            setLabel(selectedComponent.label || selectedComponent.type);
            const currentProperties = selectedComponent.properties || {};
            setProperties(currentProperties);
            setPropertiesJson(JSON.stringify(currentProperties, null, 2)); // Pretty print JSON
        } else {
            setProperties({});
            setLabel('');
            setPropertiesJson('{}');
        }
    }, [selectedComponent]);

    // Handle label changes, with a generic auto-population if properties are empty
    const handleLabelChange = (e) => {
        const newLabel = e.target.value;
        setLabel(newLabel);

        // Check if current properties are truly empty (not just an empty object)
        const currentPropertiesAreEmpty = selectedComponent && (
            !selectedComponent.properties || Object.keys(selectedComponent.properties).length === 0
        );

        // If properties are empty when the label is first changed, provide a generic starter JSON
        if (currentPropertiesAreEmpty) {
            const genericDefaultProps = {
                name: newLabel,
                type: selectedComponent?.type,
                description: "This is a custom property. Add your own JSON here!",
                // You can add a simple nested example if you like, to hint at flexibility
                "network_config": {
                    "ip_address": "",
                    "subnet_mask": ""
                },
                "status": "active"
            };
            setProperties(genericDefaultProps);
            setPropertiesJson(JSON.stringify(genericDefaultProps, null, 2));
        }
    };

    // Handle changes in the JSON textarea
    const handlePropertiesJsonChange = (e) => {
        setPropertiesJson(e.target.value);
    };

    // Handle saving properties
    const handleSaveProperties = () => {
        if (selectedComponent && onUpdateComponent) {
            const parsedProperties = safeParseJSON(propertiesJson);

            if (parsedProperties !== null) { // Check for null after parsing for errors
                // Pass a partial component object with updated fields
                onUpdateComponent(selectedComponent.id, {
                    label: label,
                    properties: parsedProperties,
                });
                onClose(); // Close the panel after saving
            } else {
                alert("Invalid JSON in properties. Please correct it before saving.");
            }
        }
    };

    return (
        <Panel>
            <h2>Properties Panel</h2>
            {selectedComponent ? (
                <div>
                    <h3>Component: {selectedComponent.type}</h3>

                    <PropertySection>
                        <label>Label:</label>
                        <PropertyInput
                            type="text"
                            value={label}
                            onChange={handleLabelChange}
                        />
                    </PropertySection>

                    <h4>Custom Properties (JSON):</h4>
                    <PropertySection>
                        <label>Edit as JSON:</label>
                        <PropertyTextArea
                            value={propertiesJson}
                            onChange={handlePropertiesJsonChange}
                            // Simplified, flexible placeholder
                            placeholder={`{\n  "key1": "value1",\n  "nested_object": {\n    "key2": "value2"\n  },\n  "array_example": [\n    "item1",\n    "item2"\n  ]\n}`}
                        />
                        <small>Enter any valid JSON structure for component properties.</small>
                    </PropertySection>

                    <SaveButton onClick={handleSaveProperties}>Save</SaveButton>
                    <CloseButton onClick={onClose}>Close</CloseButton>
                </div>
            ) : (
                <p>Select a component to view and edit its properties.</p>
            )}
        </Panel>
    );
}

export default PropertiesPanel;